import { useState } from 'react';
import ReferenceSelect from '../../ReferenceSelect';
import { localToday } from '../../SlantStoragePanel';
import { dnaBody, runBody } from '../quickActions';
import { FormButtons, FormError, grid, stack, useQuickSubmit } from './shared';
import * as S from '../../specimens/styles';

/* What a run was sequenced on (dna_sequencing_runs.PLATFORMS). */
const PLATFORMS = ['sanger', 'nanopore', 'illumina', 'pacbio', 'other'];

/*
 * Extract DNA from one of the isolate's cultures (POST /dna): a plate or a
 * slant, never both (dna.py:303). The extract takes the culture's code as
 * its own; the date is required (dna.py:352).
 */
export function ExtractDnaForm({ plates, slants, extra = {}, users, user, t, onDone, onCancel }) {
  const sources = [
    ...plates.map((p) => ({ value: `plate:${p.plate_id}`, label: p.plate_code, taken: p.has_dna })),
    ...slants.map((s) => ({ value: `slant:${s.slant_id}`, label: s.slant_code, taken: s.has_dna })),
  ];
  const first = sources.find((s) => !s.taken) || sources[0];
  const [form, setForm] = useState({
    source: extra.source || first?.value || '', date_performed: localToday(), performed_by: user?.user_id || '',
    laboratory: '', extraction_kit: '', concentration_ng_ul: '', a260_280: '', notes: '',
  });
  const { busy, error, setError, send } = useQuickSubmit('hub-dna');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.source) { setError(t('quick.problems.sourceRequired')); return; }
    if (!form.date_performed) { setError(t('quick.problems.dateRequired')); return; }
    const res = await send('post', '/dna', dnaBody(form));
    if (res) onDone(t('toast.dnaCreated', { code: res.data?.isolate_code || '' }));
  };

  if (!sources.length) return <p style={S.hint}>{t('quick.noCultures')}</p>;
  return (
    <form onSubmit={submit} style={stack} data-testid="quick-extract">
      <label style={S.label}>
        {t('quick.dna.source')} *
        <select value={form.source} onChange={set('source')} style={S.input} title={t('tip.quick.dnaSource')}
          data-testid="quick-dna-source">
          {sources.map((s) => <option key={s.value} value={s.value}>{s.label}{s.taken ? ` · ${t('quick.dna.taken')}` : ''}</option>)}
        </select>
      </label>
      <div style={grid}>
        <label style={S.label}>
          {t('quick.dna.date')} *
          <input type="date" value={form.date_performed} onChange={set('date_performed')} max={localToday()}
            style={S.input} title={t('tip.quick.dnaDate')} aria-required="true" />
        </label>
        <label style={S.label}>
          {t('quick.dna.performedBy')}
          <select value={form.performed_by} onChange={set('performed_by')} style={S.input} title={t('tip.quick.performedBy')}>
            <option value="">—</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
          </select>
        </label>
        <label style={S.label}>
          {t('quick.dna.laboratory')}
          <input value={form.laboratory} onChange={set('laboratory')} maxLength={200} style={S.input} title={t('tip.quick.laboratory')} />
        </label>
        <label style={S.label}>
          {t('quick.dna.kit')}
          <input value={form.extraction_kit} onChange={set('extraction_kit')} maxLength={120} style={S.input} title={t('tip.quick.kit')} />
        </label>
        <label style={S.label}>
          {t('quick.dna.concentration')}
          <input type="number" min="0" max="100000" step="0.1" value={form.concentration_ng_ul}
            onChange={set('concentration_ng_ul')} style={S.input} title={t('tip.quick.concentration')} />
        </label>
        <label style={S.label}>
          {t('quick.dna.purity')}
          <input type="number" min="0" max="5" step="0.01" value={form.a260_280} onChange={set('a260_280')}
            style={S.input} title={t('tip.quick.purity')} />
        </label>
      </div>
      <label style={S.label}>
        {t('quick.notes')}
        <textarea rows={2} value={form.notes} onChange={set('notes')} style={S.input} title={t('tip.quick.notes')} />
      </label>
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t} saveLabel={t('quick.dna.save')} />
    </form>
  );
}

/*
 * Add a sequencing run to an extract (POST /dna/{id}/runs). The gene is
 * required (dna_sequencing_runs.py:328); the reference lists are the DNA
 * page's own, and grow with "+ New".
 */
export function AddRunForm({ extracts, extra = {}, t, onDone, onCancel }) {
  const [dnaId, setDnaId] = useState(extra.dnaId || extracts[0]?.dna_id || '');
  const [form, setForm] = useState({
    amplification_gene_id: '', amplification_primers_id: '', sequencing_primers_id: '', sequencing_provider_id: '',
    date_sent_for_sequencing: localToday(), platform: '', barcode: '', notes: '',
  });
  const { busy, error, setError, send } = useQuickSubmit('hub-run');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amplification_gene_id) { setError(t('quick.problems.geneRequired')); return; }
    const res = await send('post', `/dna/${dnaId}/runs`, runBody(form));
    if (res) onDone(t('toast.runAdded', { code: res.data?.run_code || '' }));
  };

  if (!extracts.length) return <p style={S.hint}>{t('quick.noExtracts')}</p>;
  return (
    <form onSubmit={submit} style={stack} data-testid="quick-run">
      <label style={S.label}>
        {t('quick.run.extract')}
        <select value={dnaId} onChange={(e) => setDnaId(e.target.value)} style={S.input} title={t('tip.quick.extract')}>
          {extracts.map((d) => <option key={d.dna_id} value={d.dna_id}>{d.isolate_code}</option>)}
        </select>
      </label>
      <div style={grid}>
        <label style={S.label}>
          {t('quick.run.gene')} *
          <ReferenceSelect category="genes" value={form.amplification_gene_id} onChange={pick('amplification_gene_id')}
            title={t('tip.quick.gene')} />
        </label>
        <label style={S.label}>
          {t('quick.run.ampPrimers')}
          <ReferenceSelect category="primers" value={form.amplification_primers_id}
            onChange={pick('amplification_primers_id')} title={t('tip.quick.ampPrimers')} />
        </label>
        <label style={S.label}>
          {t('quick.run.seqPrimers')}
          <ReferenceSelect category="primers" value={form.sequencing_primers_id}
            onChange={pick('sequencing_primers_id')} title={t('tip.quick.seqPrimers')} />
        </label>
        <label style={S.label}>
          {t('quick.run.provider')}
          <ReferenceSelect category="providers" value={form.sequencing_provider_id}
            onChange={pick('sequencing_provider_id')} title={t('tip.quick.provider')} />
        </label>
        <label style={S.label}>
          {t('quick.run.sent')}
          <input type="date" value={form.date_sent_for_sequencing} onChange={set('date_sent_for_sequencing')}
            style={S.input} title={t('tip.quick.sent')} />
        </label>
        <label style={S.label}>
          {t('quick.run.platform')}
          <select value={form.platform} onChange={set('platform')} style={S.input} title={t('tip.quick.platform')}>
            <option value="">—</option>
            {PLATFORMS.map((p) => <option key={p} value={p}>{t(`dna:platforms.${p}`)}</option>)}
          </select>
        </label>
        <label style={S.label}>
          {t('quick.run.barcode')}
          <input value={form.barcode} onChange={set('barcode')} maxLength={60} dir="ltr" style={S.input}
            title={t('tip.quick.barcode')} />
        </label>
      </div>
      <label style={S.label}>
        {t('quick.notes')}
        <textarea rows={2} value={form.notes} onChange={set('notes')} style={S.input} title={t('tip.quick.notes')} />
      </label>
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t} saveLabel={t('quick.run.save')} />
    </form>
  );
}
